<script lang="typescript">
  // packages
  import { geoIdentity, geoPath } from 'd3-geo';
  import { onMount } from 'svelte';

  // components
  import MapKey from '../MapKey.svelte';

  // utils
  import { createHatchFill, getColor, NEUTRAL, TIE } from '../../colors';
  import scaleCanvas from '../../utils/scale-canvas';

  // types
  import type { Feature, FeatureCollection, MultiLineString } from 'geojson';
  import type { StateInterface } from '../../interfaces';

  // props
  export let geography: {
    collection: FeatureCollection;
    innerlines: MultiLineString;
  };
  export let data: Map<string, StateInterface>;

  // state
  let width: number;
  let height: number;
  let container: HTMLDivElement;
  let canvas: HTMLCanvasElement;
  let context: CanvasRenderingContext2D;

  // constants
  const dpi = window.devicePixelRatio || 1;
  const margin = 5;
  const projection = geoIdentity().reflectY(true);
  const path = geoPath(projection);

  onMount(() => {
    context = canvas.getContext('2d') as CanvasRenderingContext2D;
    path.context(context);
    context.scale(dpi, dpi);
    context.translate(margin, margin);
    getBounds();
    render(data);
  });

  function render(lookup: Map<string, StateInterface>) {
    if (!context) return;

    context.clearRect(0, 0, width, height);

    context.lineCap = 'round';
    context.lineJoin = 'round';

    for (let idx = 0; idx < geography.collection.features.length; idx++) {
      const feature = geography.collection.features[idx];
      const fillStyle = getFillStyle(feature, lookup);

      context.beginPath();
      path(feature);
      context.fillStyle = fillStyle;
      context.fill();
      context.closePath();
    }

    context.beginPath();
    path(geography.innerlines);
    context.strokeStyle = 'rgba(255, 255, 255, 0.65)';
    context.lineWidth = 0.6;
    context.stroke();
    context.closePath();
  }

  function getFillStyle(feature: Feature, lookup: Map<string, StateInterface>) {
    const code: string = feature.properties!.code;
    const subunit = lookup.get(code);

    let color: string;

    if (!subunit) {
      color = NEUTRAL;
    } else if (subunit.total_votes <= 0) {
      color = NEUTRAL;
    } else {
      const [leader, second] = subunit.results;

      if (leader.votes === second.votes) {
        color = TIE;
      } else {
        const partyColor = getColor(leader.party);

        color = leader.is_winner ? partyColor : createHatchFill(partyColor);
      }
    }

    return color;
  }

  function getBounds() {
    const bounds = container.getBoundingClientRect();

    width = bounds.width;
    height = bounds.height;

    projection.fitSize(
      [width - margin - margin, height - margin - margin],
      geography.collection
    );

    // canvas
    scaleCanvas(context, width, height, margin);
  }

  function onResize() {
    getBounds();
    render(data);
  }

  $: render(data);
</script>

<svelte:window on:resize|passive={onResize} />

<div bind:this={container} class="flex-1 position-relative">
  <canvas bind:this={canvas} class="position-absolute top-0 left-0" />
</div>

<MapKey />
